import { FC, Fragment } from 'react';
import { Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DetailsRow } from 'lib/ClientDataDetails/DetailsRow';
import { formatStreetForPerson } from 'utils/formatAddress/formatAddress';
import TemplateDetails from '../TemplateDetails';
import formatFullName from 'utils/formatFullName/formatFullName';
import formatCityName from 'utils/formatCityName/formatCityName';
import { ClientDetailsPerson } from 'types/ClientDetails/ClientDetailsPerson';

type ContactDetailsProps = {
  persons: ClientDetailsPerson[];
};

const ContactDetails: FC<ContactDetailsProps> = ({ persons }) => {
  const { t } = useTranslation();
  const translationsRoot = 'clients.clientData.contactDetails';

  if (!persons.length) {
    return null;
  }

  return (
    <Grid container data-testid="contactDetails-container">
      {persons.map((person, index) => {
        const fullName = formatFullName(
          person.natural?.name?.initials,
          person.natural?.name?.lastNamePrefix,
          person.natural?.name?.lastName
        );
        const phoneNumbers = person.contact?.phoneNumber;
        const emailAddresses = person.contact?.emailAddress;
        return (
          <Grid xs={12} md={6} item px={3} py={2} key={`${fullName}-${index}`}>
            <TemplateDetails title={fullName}>
              <DetailsRow label={t(`${translationsRoot}.emailAddress`)}>
                <SingleValue value={emailAddresses} titleRoot={`${translationsRoot}.emailType`} />
              </DetailsRow>
              <DetailsRow label={t(`${translationsRoot}.phoneNumber`)}>
                <SingleValue value={phoneNumbers} titleRoot={`${translationsRoot}.phoneType`} />
              </DetailsRow>
              <DetailsRow
                type="string"
                label={t(`${translationsRoot}.address`)}
                value={formatStreetForPerson(person) ?? '-'}
              />
              <DetailsRow
                type="string"
                label={t(`${translationsRoot}.postalCode`)}
                value={person.contact?.address?.postalCode}
              />
              <DetailsRow
                type="string"
                label={t(`${translationsRoot}.city`)}
                value={formatCityName(person.contact?.address?.city)}
              />
              <DetailsRow
                type="string"
                label={t(`${translationsRoot}.country`)}
                value={person.contact?.address?.country?.displayName}
              />
            </TemplateDetails>
          </Grid>
        );
      })}
    </Grid>
  );
};

type SingleValueProps = {
  value?: string;
  titleRoot: string;
};

const SingleValue = ({ value, titleRoot }: SingleValueProps) => {
  const { t } = useTranslation();

  if (!value) {
    return <>-</>;
  }

  return (
    <>
      <Fragment key={`${titleRoot}${value}`}>
        <span title={t(`${titleRoot}${value?.toLowerCase()}`)}>{value}</span>
        <br />
      </Fragment>
    </>
  );
};

export default ContactDetails;
